import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { AngularFireFunctions } from '@angular/fire/compat/functions';
import { doc, Firestore, getDoc, onSnapshot } from '@angular/fire/firestore';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CatchErrorService } from '@core/catch-error.service';
import { LoadingService } from '@core/loading.service';
import {
  buildPushToastPayload,
  normalizePushConstantsToast,
  readLanguageCodeFromStorage,
  resolvePushToastLanguage,
} from '@core/push-notification-toast.i18n';
import { BehaviorSubject, from, Observable, of, shareReplay, switchMap } from 'rxjs';
import { CommonVariable } from 'src/app/app.constants';
import { MyToastbarComponent } from 'src/app/components/my-toastbar/my-toastbar.component';

@Injectable({
  providedIn: "root",
})
export class UserVersionService {
  selectedlanguage$: BehaviorSubject<string>;
  referralData;
  invitationData: { psId: ""; member_id: "" };

  constructor(
    private firestore: Firestore,
    private catchService: CatchErrorService,
    private snackBar: MatSnackBar,
    private loadingService: LoadingService,
    public angularFireFunctions: AngularFireFunctions,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    let languageCode = "";
    // Ensure localStorage is only accessed in the browser
    if (isPlatformBrowser(this.platformId) && typeof localStorage !== "undefined" && localStorage !== null) {
      languageCode = localStorage.getItem("language_code") || "";
    }
    this.selectedlanguage$ = new BehaviorSubject<string>(languageCode);
  }

  public showOnesignalIosToast(): void {
    const lang = resolvePushToastLanguage(
      this.selectedlanguage$?.value,
      readLanguageCodeFromStorage()
    );
    const defaultMsg = buildPushToastPayload("add_to_home_screen_push", lang, {
      duration_ms: 5000,
      icon: "tick circle error.svg",
      type: "Error",
    });
    this.getConstantsMsg("1143")
      .then((translatedMsg) => {
        const payload = normalizePushConstantsToast(translatedMsg, defaultMsg);
        this.snackBar.openFromComponent(MyToastbarComponent, {
          duration: Number(payload.style?.duration_ms ?? payload.style?.duration ?? 5000),
          horizontalPosition: "center",
          verticalPosition: "top",
          panelClass: "toast-for-syndicate",
          data: payload,
        });
      })
      .catch(() => {
        this.snackBar.openFromComponent(MyToastbarComponent, {
          duration: 5000,
          horizontalPosition: "center",
          verticalPosition: "top",
          panelClass: "toast-for-syndicate",
          data: defaultMsg,
        });
      });
  }
  public getConstantsMsg(msgCode) {
    return new Promise<any>((resolve, reject) => {
      try {
        // Reference to the document in Firestore
        const docRef = doc(this.firestore, `/Global_lookup/toast_message/list/${msgCode}`);
        getDoc(docRef).then((msg) => {
          // Check if the document exists
          const msgExists = msg.exists();
          const msgData = msg.data();

          // This Condition checks if the message data is not null or undefined
          if (msgExists != null && msgExists !== undefined && msgData != null && msgData !== undefined) {
            const msgDataJSON = JSON.parse(JSON.stringify(msg.data())); // Parse JSON if necessary
            let toastMsg: string;

            // Resolve the message based on language preferences
            if (msgDataJSON?.message?.[this.selectedlanguage$?.value]) {
              toastMsg = msgDataJSON.message[this.selectedlanguage$?.value];
            } else if (msgDataJSON?.message?.[CommonVariable?.preferredLanguage]) {
              toastMsg = msgDataJSON.message[CommonVariable?.preferredLanguage];
            }

            const msgStyle = msgDataJSON.style;
            const toastMsgData = {
              message: toastMsg ? toastMsg : {},
              style: msgStyle,
            };
            resolve(toastMsgData);
          } else {
            resolve(null); // No message data found
          }
        });
      } catch (err) {
        reject(err); // Reject promise on error
      }
    });
  }


  // Get data from Angular firestore
  getFirestoreData(url: string, method: string) {
    return this.catchService.isOnline$.pipe(
      switchMap((isOnline) => {
        if (!isOnline) {
          return of(null); // Handle offline case gracefully
        }
        const documentRef = doc(this.firestore, url);
        if (method === "get") {
          return from(getDoc(documentRef)).pipe(
            this.catchService.retryWhen,
            this.catchService.catchOfflineError
          );
        }
        if (method === "snapshotChanges") {
          return new Observable((subscriber) => {
            const unsubscribe = onSnapshot(
              documentRef,
              (snapshot) => subscriber.next(snapshot.data()),
              (error) => subscriber.error(error)
            );
            return () => unsubscribe();
          }).pipe(
            this.catchService.retryWhen,
            this.catchService.catchOfflineError
          );
        }
        return of(null); // Handle unsupported methods gracefully
      }),
      this.catchService.retryWhen,
      this.catchService.catchOfflineError,
      shareReplay(1)
    );
  }

  public showToastMessage(translatedMsg, defaultMsg): void {
    const message = translatedMsg?.message ? translatedMsg?.message : defaultMsg?.message;
    const icon = translatedMsg?.style?.icon ? translatedMsg?.style?.icon : defaultMsg?.style?.icon;
    const snackType = translatedMsg?.style?.type ? translatedMsg?.style?.type : defaultMsg?.style?.type;
    const duration = translatedMsg?.style?.duration ? translatedMsg?.style?.duration : 2000;
    this.snackBar.openFromComponent(MyToastbarComponent, {
      duration: duration,
      horizontalPosition: "center",
      verticalPosition: "top",
      panelClass: "toast-for-syndicate",
      data: { message: message, duration: duration, icon: icon, state: snackType },
    });
  }

  getPerformanceGroupRequirements() {
    const performanceGroupDocRef = doc(this.firestore, "/Global_lookup/performance_group_requirement");
    return from(getDoc(performanceGroupDocRef).then((snapshot) => snapshot.data()));
  }

  setReferralData(custrefID, referralCode) {
    this.referralData = {
      custrefID: custrefID,
      referralCode: referralCode,
    };
  }

  // This function sets the invitation data, which is used to invite someone to join a group.
  // The invitation data includes the id of the group and the id of the member being invited.
  setInvitationData(psId, member_id) {
    this.invitationData = {
      psId: psId,
      member_id: member_id,
    };
  }





}
